import { useState } from 'react';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  DollarSign,
  Heart,
  Lock,
  Rocket,
  Crown,
  ChevronDown,
  ChevronUp,
  Info,
} from 'lucide-react';

/**
 * MonetizeToggle — unified monetization panel for upload forms.
 *
 * Props:
 *   monetizeConfig   – { enabled, tip_jar, pay_to_unlock, unlock_price, subscription_only, boost_after }
 *   setMonetizeConfig – setter
 *   lockConfig       – existing content-lock config (passed through)
 *   setLockConfig    – setter for content-lock
 *   compact          – smaller footprint for track/video forms
 */
export default function MonetizeToggle({
  monetizeConfig,
  setMonetizeConfig,
  lockConfig,
  setLockConfig,
  compact = false,
}) {
  const [expanded, setExpanded] = useState(false);
  const enabled = monetizeConfig?.enabled ?? false;

  const update = (patch) =>
    setMonetizeConfig((prev) => ({ ...prev, ...patch }));

  const handleToggle = (checked) => {
    if (!checked) {
      // Turning off — clear all
      setMonetizeConfig({ enabled: false, tip_jar: false, pay_to_unlock: false, unlock_price: '', subscription_only: false, boost_after: false });
      if (setLockConfig) setLockConfig(null);
    } else {
      setMonetizeConfig({ enabled: true, tip_jar: true, pay_to_unlock: false, unlock_price: '', subscription_only: false, boost_after: false });
    }
  };

  const handlePayToggle = (checked) => {
    update({ pay_to_unlock: checked, subscription_only: false });
    if (setLockConfig) {
      setLockConfig(checked ? { lock_type: 'pay', price: monetizeConfig?.unlock_price || '' } : null);
    }
  };

  const handlePriceChange = (val) => {
    const price = parseFloat(val) || '';
    update({ unlock_price: price });
    if (setLockConfig && monetizeConfig?.pay_to_unlock) {
      setLockConfig({ lock_type: 'pay', price });
    }
  };

  const handleSubToggle = (checked) => {
    update({ subscription_only: checked, pay_to_unlock: false, unlock_price: '' });
    if (setLockConfig) {
      setLockConfig(checked ? { lock_type: 'subscribe' } : null);
    }
  };

  return (
    <div
      className={`border rounded-xl transition-all ${
        enabled
          ? 'border-emerald-500/30 bg-emerald-500/5'
          : 'border-border bg-card/50'
      } ${compact ? 'p-3' : 'p-4'}`}
      data-testid="monetize-toggle"
    >
      {/* Main Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <DollarSign className={`w-4.5 h-4.5 ${enabled ? 'text-emerald-400' : 'text-muted-foreground'}`} />
          <div>
            <Label className="text-sm font-semibold cursor-pointer" htmlFor="monetize-master">
              Monetize This Content
            </Label>
            {!compact && (
              <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                Enable tips, pay-to-unlock, or subscriber-only access
              </p>
            )}
          </div>
        </div>
        <Switch
          id="monetize-master"
          checked={enabled}
          onCheckedChange={handleToggle}
          data-testid="monetize-master-toggle"
        />
      </div>

      {/* Expanded Options */}
      {enabled && (
        <div className="mt-3 space-y-2.5 animate-in fade-in slide-in-from-top-1 duration-200">
          {/* Tip Jar */}
          <div className="flex items-center justify-between p-2.5 rounded-lg bg-card/80 border border-border">
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-pink-400" />
              <div>
                <p className="text-sm font-medium text-foreground">Tip Jar</p>
                <p className="text-[10px] text-muted-foreground">Fans can tip $1, $5, $10, or custom</p>
              </div>
            </div>
            <Switch
              checked={monetizeConfig?.tip_jar ?? true}
              onCheckedChange={(v) => update({ tip_jar: v })}
              data-testid="monetize-tip-jar-toggle"
            />
          </div>

          {/* Pay to Unlock */}
          <div className={`p-2.5 rounded-lg border transition-all ${
            monetizeConfig?.pay_to_unlock
              ? 'bg-emerald-500/5 border-emerald-500/20'
              : 'bg-card/80 border-border'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-emerald-400" />
                <div>
                  <p className="text-sm font-medium text-foreground">Pay to Unlock</p>
                  <p className="text-[10px] text-muted-foreground">Set a price to view this content</p>
                </div>
              </div>
              <Switch
                checked={monetizeConfig?.pay_to_unlock ?? false}
                onCheckedChange={handlePayToggle}
                disabled={monetizeConfig?.subscription_only}
                data-testid="monetize-pay-unlock-toggle"
              />
            </div>
            {monetizeConfig?.pay_to_unlock && (
              <div className="flex items-center gap-2 mt-2 pl-6">
                <span className="text-muted-foreground text-sm">$</span>
                <Input
                  type="number"
                  min="0.50"
                  step="0.50"
                  value={monetizeConfig?.unlock_price || ''}
                  onChange={(e) => handlePriceChange(e.target.value)}
                  placeholder="5.00"
                  className="bg-muted/50 border-border h-8 text-sm w-28"
                  data-testid="monetize-unlock-price"
                />
                <span className="text-xs text-muted-foreground">USD</span>
              </div>
            )}
          </div>

          {/* Subscription Only */}
          <div className="flex items-center justify-between p-2.5 rounded-lg bg-card/80 border border-border">
            <div className="flex items-center gap-2">
              <Crown className="w-4 h-4 text-amber-400" />
              <div>
                <p className="text-sm font-medium text-foreground">Subscription Only</p>
                <p className="text-[10px] text-muted-foreground">Only your subscribers can access</p>
              </div>
            </div>
            <Switch
              checked={monetizeConfig?.subscription_only ?? false}
              onCheckedChange={handleSubToggle}
              disabled={monetizeConfig?.pay_to_unlock}
              data-testid="monetize-sub-only-toggle"
            />
          </div>

          {/* Boost Promotion */}
          <div className="flex items-center justify-between p-2.5 rounded-lg bg-card/80 border border-border">
            <div className="flex items-center gap-2">
              <Rocket className="w-4 h-4 text-violet-400" />
              <div>
                <p className="text-sm font-medium text-foreground">Boost Promotion</p>
                <p className="text-[10px] text-muted-foreground">Promote in feed after posting</p>
              </div>
            </div>
            <Switch
              checked={monetizeConfig?.boost_after ?? false}
              onCheckedChange={(v) => update({ boost_after: v })}
              data-testid="monetize-boost-toggle"
            />
          </div>

          {/* Revenue Split Info */}
          <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-muted/20 border border-border/50">
            <Info className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            <p className="text-[10px] text-muted-foreground leading-tight">
              You keep <span className="text-foreground font-semibold">90%</span> of all earnings. 10% platform fee applies.
              Payouts via Stripe Connect.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
